import NumberInput from 'components/inputs/NumberInput';
import { Field } from 'formik';
import { useCallback, type ReactNode } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { useIntl } from 'react-intl';
import { pick, inRange, get } from 'lodash';
import type { FormikProps } from 'formik';
import type { INumberInputChange } from '../NumberInput';
import { isFiniteNumber } from '../numbers';
import s from './BirthdayInputs.module.scss';

export interface IBirthdayValues {
  day?: number | null;
  month?: number | null;
  year?: number | null;
}

export interface IBirthdayInputsProps {
  form: FormikProps<IBirthdayValues>;
  label?: ReactNode;
  required?: boolean;
}

const BirthdayInputs = ({ form, label, required }: IBirthdayInputsProps) => {
  const { formatMessage: t } = useIntl();
  const { values, setFieldValue, setFieldError } = form;

  const handleSetDate = useCallback(
    ({ name, value: v }: INumberInputChange) => {
      if (!values.day || !values.month || !values.year) return;
      const date: Record<string, number | null | undefined> = pick(values, [
        'day',
        'month',
        'year',
      ]);
      date[name] = v;
      // The Date constructor reads a cleared (null) part as 0, as Number(null) is.
      const selectedDate = new Date(Number(date.year), Number(date.month) - 1, Number(date.day));
      setFieldValue('date_of_birth', selectedDate);
    },
    [setFieldValue, values],
  );

  const validateDay = useCallback(
    (day: number | null | undefined) => (required && !day ? t({ id: 'errors.fill_field' }) : null),
    [t, required],
  );

  const validateMonth = useCallback(
    (month: number | null | undefined) => {
      const year = get(values, 'year', null);
      const day = get(values, 'day', null);
      if (isFiniteNumber(day) && isFiniteNumber(month) && isFiniteNumber(year)) {
        const daysInMonth = new Date(year, month, 0).getDate();
        const validDate = inRange(day, 1, daysInMonth + 1);
        if (!validDate) {
          setFieldError('day', t({ id: 'errors.invalid_date' }));
          return t({ id: 'errors.invalid_date' });
        }
      }
      return required && !month ? t({ id: 'errors.fill_field' }) : null;
    },
    [values, t, required, setFieldError],
  );

  const validateYear = useCallback(
    (year: number | null | undefined) => {
      const currentYear = new Date().getFullYear();
      if (year && year < 1940) return t({ id: 'errors.min_value' }, { min: 1940 });
      if (year && year > currentYear) return t({ id: 'errors.max_value' }, { max: currentYear });
      const res = required && !year ? t({ id: 'errors.fill_field' }) : null;
      return res;
    },
    [t, required],
  );

  return (
    <div style={{ marginTop: '35px' }}>
      {label ? <h3 className={s.dateOfBirth}>{label}</h3> : null}
      <Row className={s.birthWrapper}>
        <Col xs={4} md={2}>
          <Field
            name="day"
            component={NumberInput}
            max={31}
            label={t({ id: 'loan.personal_financing_request.day' })}
            className={s.numberInputWrapper}
            validate={validateDay}
            sideEffectOnChange={handleSetDate}
          />
        </Col>
        <Col xs={4} md={2}>
          <Field
            name="month"
            component={NumberInput}
            max={12}
            label={t({ id: 'loan.personal_financing_request.month' })}
            className={s.numberInputWrapper}
            validate={validateMonth}
            sideEffectOnChange={handleSetDate}
          />
        </Col>
        <Col xs={4} md={2}>
          <Field
            name="year"
            component={NumberInput}
            thousandSeparator=""
            max={new Date().getFullYear()}
            label={t({ id: 'loan.personal_financing_request.year' })}
            className={s.numberInputWrapper}
            validate={validateYear}
            sideEffectOnChange={handleSetDate}
          />
        </Col>
      </Row>
    </div>
  );
};

export default BirthdayInputs;
